/* eslint-disable indent */
"use client";

import React, { useContext, useEffect, useState } from "react";
import { Calendar, Pencil } from "lucide-react";
import Image from "next/image";
import LoraCard from "@/components/atoms/LoraCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { updateImage } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { Span } from "@/utils/styling/typography.styled";
import UserCard from "components/atoms/UserCard";
import { ImageDTO, ImageMetadata, IUserDTO, RequestType } from "types";
interface MetadataSectionProps {
  imageInfo: ImageDTO | null;
}
const MetadataSection: React.FC<MetadataSectionProps> = ({
  imageInfo
}) => {
  const {
    state
  } = useContext(AppContext);
  const [editField, setEditField] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<ImageMetadata>(imageInfo?.imageMetadata || {
    npcFieldsMap: {},
    theme: "",
    premium: false,
    username: "",
    requestType: RequestType.CHARACTER,
    description: "",
    userDTO: {} as IUserDTO,
    modelData: undefined
  });
  useEffect(() => {
    if (imageInfo && metadata.description !== imageInfo.imageMetadata.description) {
      setMetadata(imageInfo.imageMetadata);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageInfo]);
  if (!imageInfo) return null;
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  const handleChange = (field: keyof ImageMetadata, value: string | boolean) => {
    setMetadata(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSave = async () => {
    if (!metadata || Object.keys(metadata).length === 0) return;
    await updateImage(imageInfo.id, metadata);
    imageInfo.imageMetadata = metadata;
    setEditField(null);
  };
  const handleCancel = (field: keyof ImageMetadata) => {
    setMetadata(prev => ({
      ...prev,
      [field]: imageInfo.imageMetadata[field]
    }));
    setEditField(null);
  };
  const userImage = state.username === imageInfo.imageMetadata.userDTO.username;
  return <Card className="!bg-background/95 backdrop-blur border-none !supports-[backdrop-filter]:!bg-background/60" data-sentry-element="Card" data-sentry-component="MetadataSection" data-sentry-source-file="index.tsx">
            <CardHeader className="pb-4 text-center border-sky-200 !border-b-1" data-sentry-element="CardHeader" data-sentry-source-file="index.tsx">
                <CardTitle className="!text-[rgb(122,168,227)] text-2xl font-bold" data-sentry-element="CardTitle" data-sentry-source-file="index.tsx">
                    Image Details
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6" data-sentry-element="CardContent" data-sentry-source-file="index.tsx">
                {/* User Information */}
                <section className="space-y-4">
                    <h3 className="text-md font-semibold !text-[rgb(122,168,227)]">
                        User Details
                    </h3>
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <UserCard user={imageInfo.imageMetadata.userDTO} data-sentry-element="UserCard" data-sentry-source-file="index.tsx" />
                        <div className="flex items-center gap-2 text-sm text-center text-muted-foreground">
                            <Calendar className="h-4 w-4" data-sentry-element="Calendar" data-sentry-source-file="index.tsx" />
                            <Span className="!text-gray-400" data-sentry-element="Span" data-sentry-source-file="index.tsx">Created At: </Span>
                            <span className="text-xs text-gray-400" suppressHydrationWarning>
                                {new Date(imageInfo.createdAt).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </section>

                <Separator data-sentry-element="Separator" data-sentry-source-file="index.tsx" />

                {/* Basic Details */}
                <section className="space-y-4">
                    <h3 className="text-md font-semibold !text-[rgb(122,168,227)]">
                        Basic Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-0">
                            <span className="text-xs font-500 text-gray-400">
                                Type
                            </span>
                            <p className="text-sm">
                                {formatRequestType(imageInfo.imageMetadata.requestType)}
                            </p>
                        </div>
                        <div className="space-y-0">
                            <span className="text-xs font-500 text-gray-400">
                                Premium
                            </span>
                            <p className="text-sm">
                                {imageInfo.imageMetadata.premium ? "True" : "False"}
                            </p>
                        </div>
                    </div>
                    <div className="space-y-0">
                        <span className="text-xs font-500 text-gray-400">
                            Prompt
                        </span>
                        <p className="text-sm">
                            {imageInfo.imageMetadata.theme}
                        </p>
                    </div>
                </section>

                {imageInfo.imageMetadata.description && <>
                        <Separator />
                        <section className="space-y-0">
                            <div className="flex items-center justify-between">
                                <h3 className="text-md font-semibold !text-[rgb(122,168,227)]">
                                    Description
                                </h3>
                                {!editField && userImage && <Button size="sm" variant="ghost" onClick={() => setEditField("description")}>
                                        <Pencil className="h-4 w-4 mr-2" />
                                        Edit
                                    </Button>}
                            </div>
                            {editField === "description" ? <div className="space-y-4">
                                    <Textarea className="min-h-[100px]" value={metadata.description || ""} onChange={e => handleChange("description", e.target.value)} placeholder="Enter description..." />
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={handleSave} className="!bg-[#456CAB] hover:!bg-[#4F80CC]">
                                            Save
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => handleCancel("description")} className="hover:!bg-[#2C2E52]">
                                            Cancel
                                        </Button>
                                    </div>
                                </div> : <p className="text-sm leading-relaxed">
                                    {metadata.description}
                                </p>}
                        </section>
                    </>}

                {/* NPC Fields Section */}
                {imageInfo.imageMetadata.npcFieldsMap && Object.entries(imageInfo.imageMetadata.npcFieldsMap).length > 0 && <>
                            <Separator />
                            <section className="space-y-4">
                                <h3 className="text-md !text-[rgb(122,168,227)] font-semibold">
                                    Additional Fields
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {Object.entries(imageInfo.imageMetadata.npcFieldsMap).map(([key, value]) => value && <div key={key} className="space-y-0">
                                                    <span className="text-xs font-500 text-gray-400">
                                                        {key}
                                                    </span>
                                                    {key === "ControlNet Image" ? <div className="relative aspect-square w-full overflow-hidden rounded-lg">
                                                            <Image src={value} alt="ControlNet Image" layout="fill" objectFit="cover" className="transition-transform hover:scale-105" />
                                                        </div> : <p className="text-sm">
                                                            {value}
                                                        </p>}
                                                </div>)}
                                </div>
                            </section>
                        </>}

                {/* Lora Information */}
                {imageInfo.loraList && imageInfo.loraList.length > 0 && <>
                        <Separator />
                        <section className="space-y-4">
                            <h3 className="text-md font-semibold !text-[rgb(122,168,227)]">
                                Lora Information
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {imageInfo.loraList.map(lora => <div key={lora.loraName} className="max-w-[300px]">
                                        <LoraCard lora={lora} />
                                    </div>)}
                            </div>
                        </section>
                    </>}

                {/* Model Information */}
                {imageInfo.imageMetadata.modelData && <>
                        <Separator />
                        <section className="space-y-4">
                            <h3 className="text-md font-semibold !text-[rgb(122,168,227)]">
                                Model Information
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-0">
                                    <span className="text-xs font-500 text-gray-400">
                                        Model
                                    </span>
                                    <p className="text-sm">
                                        {imageInfo.imageMetadata.modelData.model}
                                    </p>
                                </div>
                                <div className="space-y-0">
                                    <span className="text-xs font-500 text-gray-400">
                                        CFG Scale
                                    </span>
                                    <p className="text-sm">
                                        {imageInfo.imageMetadata.modelData.cfg}
                                    </p>
                                </div>
                                <div className="space-y-0">
                                    <span className="text-xs font-500 text-gray-400">
                                        Steps
                                    </span>
                                    <p className="text-sm">
                                        {imageInfo.imageMetadata.modelData.steps}
                                    </p>
                                </div>
                                <div className="space-y-0">
                                    <span className="text-xs font-500 text-gray-400">
                                        Sampler
                                    </span>
                                    <p className="text-sm">
                                        {imageInfo.imageMetadata.modelData.sampler}
                                    </p>
                                </div>
                            </div>
                        </section>
                    </>}
            </CardContent>
        </Card>;
};
export default React.memo(MetadataSection);